import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import ClientDropdown from '@src/components/ClientDropdown';
import paths from '@src/paths';
import { useMessage } from '@src/context/common';
import {
  fetchLocalAdBatches,
  fetchLocalPlan,
  lockLocalPlan,
  publishLocalAdBatch,
  rebuildLocalAds,
  saveLocalStartBudgets,
  updateLocalPage,
} from '@src/api/localAds';
import { refreshFacebookPages } from '@src/api/facebookPages';

// The floor is measured in owner-occupied homes, not people — a renter is not
// a customer. A lower floor gives more cities their own ad set.
const FLOORS = [50000, 35000, 25000, 20000, 15000, 12500, 10000, 7500, 5000];

// An ad set holding this much of its campaign leaves nothing inside the
// campaign to absorb a downturn in that one city.
const CONCENTRATION_FLAG = 0.35;

const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const num = (n) => (n || 0).toLocaleString();
const floorLabel = (f) => (f >= 1000 ? `${f / 1000}k` : `${f}`);
const pct = (part, whole) => (whole ? `${Math.round((part / whole) * 100)}%` : '—');

// Batches still needing a person: not yet published, publishing now, or with
// ads that failed and can be retried.
const OPEN_BATCH = ['pending', 'publishing', 'failed'];
// A live publish runs on the server in the background; the page re-reads the
// batches at this interval until it finishes.
const POLL_MS = 5000;

function batchSummary(batch) {
  const rows = batch.rows || [];
  return {
    ads: rows.length,
    live: rows.filter((r) => r.status === 'published').length,
    failed: rows.filter((r) => r.status === 'failed'),
    videos: rows.reduce((sum, r) => sum + (r.videos || 0), 0),
  };
}

function concentration(campaign) {
  const adSets = campaign.ad_sets || [];
  const total = adSets.reduce((sum, a) => sum + (a.population || 0), 0);
  if (!total) return null;
  const top = adSets.reduce((a, b) => ((a.population || 0) >= (b.population || 0) ? a : b));
  return { share: (top.population || 0) / total, city: top.city };
}

export default function LocalAdBuilderPage() {
  const message = useMessage();

  // ClientDropdown emits the client's id, not the object.
  const [searchParams, setSearchParams] = useSearchParams();
  const [clientId, setClientId] = useState(searchParams.get('client') || null);
  const [adAccountId, setAdAccountId] = useState('');
  const [floorIndex, setFloorIndex] = useState(FLOORS.indexOf(7500));
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locking, setLocking] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [batches, setBatches] = useState([]);

  const floor = FLOORS[floorIndex];

  const loadPlan = useCallback(async () => {
    if (!clientId) {
      setPlan(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await fetchLocalPlan({ client_id: clientId, city_floor: floor });
      setPlan(data);
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not load the plan.');
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, floor, message]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // Default to the client's busiest account; the server already sorts by
  // recent spend. Reset when the client changes.
  useEffect(() => {
    const accounts = plan?.ad_accounts || [];
    if (!accounts.length) {
      setAdAccountId('');
      return;
    }
    setAdAccountId((current) =>
      accounts.some((a) => a.account_id === current) ? current : accounts[0].account_id,
    );
  }, [plan?.ad_accounts]);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await fetchLocalAdBatches();
      setBatches(Array.isArray(data) ? data : data?.results || []);
    } catch {
      // Secondary panel — a failure here should not take the page down.
      setBatches([]);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const campaigns = useMemo(
    () => (plan?.verticals || []).flatMap((v) => v.campaigns || []),
    [plan],
  );
  const totalMinimum = useMemo(
    () => campaigns.reduce((sum, c) => sum + (c.min_daily_budget_cents || 0), 0),
    [campaigns],
  );

  const lockedPlans = plan?.locked || [];
  const locked = lockedPlans.length > 0;
  const lockedCities = lockedPlans.reduce((sum, p) => sum + (p.cities || 0), 0);
  const withAds = lockedPlans.reduce((sum, p) => sum + (p.with_ads || 0), 0);
  const pendingAds = lockedPlans.reduce((sum, p) => sum + (p.pending || 0), 0);
  const lockedFloor = lockedPlans[0]?.city_floor;

  // Open on the floor the plan was locked at, so the preview matches what exists.
  // Declared after lockedFloor: its dependency list is read during render.
  useEffect(() => {
    if (lockedFloor && !changingPlan && FLOORS.includes(lockedFloor)) {
      setFloorIndex(FLOORS.indexOf(lockedFloor));
    }
  }, [lockedFloor, changingPlan]);

  // Re-locking is refused once creative has been sent against the plan.
  const canChangePlan = locked && withAds === 0 && pendingAds === 0;
  const showPlanner = !locked || changingPlan;
  // Matched by client id, not name: a deep link (?client=) seeds the id alone,
  // and the batches have to show up without re-picking the client.
  // A batch whose every ad was superseded by a newer push (or is already
  // live) has nothing left to publish, so it is not shown — Express's first
  // push, replaced wholesale after a hosting fix, sat on the page for hours.
  const clientBatches = batches.filter(
    (b) => OPEN_BATCH.includes(b.status)
      && (b.rows || []).some((r) => r.client != null && String(r.client) === String(clientId))
      && (b.status === 'publishing'
        || (b.rows || []).some((r) => r.status === 'pending' || r.status === 'failed')),
  );
  const publishing = clientBatches.some((b) => b.status === 'publishing');
  // What already happened, so a batch that vanished from the list above is
  // accounted for: published, or replaced wholesale by a newer push.
  const finishedBatches = batches
    .filter((b) => !clientBatches.includes(b)
      && (b.rows || []).some((r) => r.client != null && String(r.client) === String(clientId)))
    .map((b) => {
      const rows = b.rows || [];
      const live = rows.filter((r) => r.status === 'published').length;
      const replaced = rows.length > 0 && rows.every((r) => ['superseded', 'replaced'].includes(r.status));
      return { ...b, live, outcome: replaced ? 'replaced' : b.status === 'published' ? 'published' : null };
    })
    .filter((b) => b.outcome)
    .slice(0, 20);

  useEffect(() => {
    if (!publishing) return undefined;
    const timer = setInterval(loadBatches, POLL_MS);
    return () => clearInterval(timer);
  }, [publishing, loadBatches]);

  // Say when a background publish finishes, since nobody is waiting on a request.
  const lastStatus = useRef({});
  useEffect(() => {
    const previous = lastStatus.current;
    const next = {};
    let finished = false;
    batches.forEach((b) => {
      next[b.id] = b.status;
      if (previous[b.id] !== 'publishing' || b.status === 'publishing') return;
      finished = true;
      const s = batchSummary(b);
      if (b.status === 'published') {
        message.success(`Batch ${b.external_id}: ${num(s.live)} ads published.`);
      } else {
        message.error(
          b.notes || `Batch ${b.external_id}: ${num(s.failed.length)} of ${num(s.ads)} ads failed to publish.`,
        );
      }
    });
    lastStatus.current = next;
    if (finished) loadPlan();
  }, [batches, message, loadPlan]);

  const handleLock = async () => {
    if (!clientId || !adAccountId) return;
    setLocking(true);
    try {
      const { data } = await lockLocalPlan({
        client_id: clientId,
        ad_account_id: adAccountId,
        city_floor: floor,
      });
      const cities = (data.locked || []).reduce((sum, p) => sum + (p.cities || 0), 0);
      message.success(`Locked ${num(cities)} cities. Nothing was created on Facebook.`);
      setChangingPlan(false);
      loadPlan();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not lock the plan.');
    } finally {
      setLocking(false);
    }
  };

  const handleToggleLocalPage = async (pageId, verticalId, use) => {
    try {
      const { data } = await updateLocalPage({
        client_id: clientId, page_id: pageId, use_for_local: use, vertical_id: verticalId,
      });
      if (data?.warning) message.warning(data.warning);
      else message.success(use ? 'Added to this vertical’s local Pages.' : 'No longer used for this vertical’s local ads.');
      loadPlan();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not change that Page.');
    }
  };

  const handleRenamePage = async (pageId, nickname) => {
    try {
      await updateLocalPage({ client_id: clientId, page_id: pageId, nickname });
      loadPlan();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not save the nickname.');
    }
  };

  const [refreshingPages, setRefreshingPages] = useState(false);
  const handleRefreshPages = async () => {
    setRefreshingPages(true);
    try {
      const { data } = await refreshFacebookPages({ client_id: clientId });
      message.success(
        data.mismatched
          ? `Read from Facebook. ${data.mismatched} Page(s) look like they belong to another client.`
          : "Read this client's Pages from Facebook.",
      );
      loadPlan();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not read Facebook.');
    } finally {
      setRefreshingPages(false);
    }
  };

  const [rebuilding, setRebuilding] = useState(false);
  const handleRebuild = async (planId) => {
    setRebuilding(true);
    try {
      const { data } = await rebuildLocalAds({ plan_id: planId });
      message.success(`Staged ${num(data.ads)} ads to rebuild. Publish that batch below when ready.`);
      loadBatches();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not stage the rebuild.');
    } finally {
      setRebuilding(false);
    }
  };

  const handlePublish = async (batch, dryRun) => {
    try {
      const { data } = await publishLocalAdBatch(batch.id, { dry_run: dryRun });
      message.success(
        dryRun
          ? `Dry run: ${data.published} ads would publish, ${data.failed} would fail. Nothing was published.`
          : `Publishing ${num(data.ads)} ads in the background. This page updates as they go live.`,
      );
      loadBatches();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Publishing failed.');
    }
  };

  // Shown in step 1 while planning, and in step 3 once locked — the Page is
  // used at publish time, so it has to stay changeable after the lock.
  const pagePicker = plan ? (
    plan.pages?.length ? (
      <PagePicker
        clientId={clientId}
        pages={plan.pages}
        verticals={plan.verticals || []}
        split={plan.page_split}
        candidates={plan.page_candidates || []}
        locked={locked}
        onToggle={handleToggleLocalPage}
        onRename={handleRenamePage}
        onRefresh={handleRefreshPages}
        refreshing={refreshingPages}
      />
    ) : (
      <Alert severity="warning" sx={{ mt: 3 }}>
        This client has no Facebook Pages on record, so local ads have nowhere to publish.{' '}
        <RouterLink to={`${paths.facebook.pages}?client=${clientId}`}>Add a Page</RouterLink>
      </Alert>
    )
  ) : null;

  return (
    <Stack spacing={3} sx={{ p: 3, maxWidth: 1100 }}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Local Ad Builder
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
          Local campaigns go live in three steps: you lock which cities get an ad
          set, the creative app makes one ad per city, and you publish. Nothing
          reaches Facebook until you press Publish, and campaigns land paused.
        </Typography>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 2 }}>
              <ClientDropdown
                value={clientId || ''}
                onChange={(value) => {
                  setClientId(value || null);
                  const next = new URLSearchParams(searchParams);
                  if (value) next.set('client', value);
                  else next.delete('client');
                  setSearchParams(next, { replace: true });
                }}
                label="Client"
              />
            </Box>
            <FormControl size="small" sx={{ flex: 1 }} disabled={!plan?.ad_accounts?.length}>
              <InputLabel id="local-ad-account">Ad account</InputLabel>
              <Select
                labelId="local-ad-account"
                label="Ad account"
                value={adAccountId}
                onChange={(e) => setAdAccountId(e.target.value)}
              >
                {(plan?.ad_accounts || []).map((a) => (
                  <MenuItem key={a.account_id} value={a.account_id}>
                    {a.account_name}
                    {a.spend_30d > 0 ? ` — $${Math.round(a.spend_30d).toLocaleString()} last 30d` : ' — no recent spend'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {clientId && plan && !plan.ad_accounts?.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This client has no active Facebook ad account linked, so a plan can't be locked.
            </Alert>
          )}
        </CardContent>
      </Card>

      {!clientId && (
        <Alert severity="info">Pick a client to start.</Alert>
      )}

      {clientId && (
        <>
          {/* ── Step 1 ───────────────────────────────────────────── */}
          <Step
            n={1}
            title="Lock the plan"
            state={locked && !changingPlan ? 'done' : 'todo'}
            stateLabel={
              locked
                ? `Locked · ${num(lockedCities)} cities at ${num(lockedFloor)} homes`
                : 'Not locked'
            }
          >
            {locked && !changingPlan ? (
              <Stack spacing={1.5} alignItems="flex-start">
                <Typography variant="body2" color="text.secondary">
                  {num(lockedCities)} cities are agreed. Nothing exists on Facebook
                  yet — each city&apos;s campaign and ad set are created when its
                  first ad is published, using the ZIPs approved at that moment.
                </Typography>
                <Tooltip
                  title={
                    canChangePlan
                      ? ''
                      : 'Creative has already been sent against this plan, so its cities can’t change without stranding those ads.'
                  }
                >
                  <span>
                    <Button size="small" disabled={!canChangePlan} onClick={() => setChangingPlan(true)}>
                      Change the plan
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  One campaign per territory, one ad set per city. Choose how big a
                  city has to be to get its own ad set — lower means more cities.
                </Typography>

                <Box sx={{ px: 1, maxWidth: 640 }}>
                  <Slider
                    value={floorIndex}
                    min={0}
                    max={FLOORS.length - 1}
                    step={1}
                    marks={FLOORS.map((f, i) => ({ value: i, label: floorLabel(f) }))}
                    onChange={(_, v) => setFloorIndex(v)}
                    aria-label="City floor"
                  />
                </Box>

                <Stack direction="row" spacing={4} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                  <Stat
                    label="Smallest city included"
                    value={`${num(floor)} homes`}
                    hint="Owner-occupied homes. Renters aren't customers, so population overstates the market."
                  />
                  <Stat label="Campaigns" value={num(plan?.campaigns)} />
                  <Stat label="City ad sets" value={num(plan?.units)} />
                  <Stat
                    label="Homes in a city ad set"
                    value={pct(plan?.covered_homes, plan?.footprint_homes)}
                    hint={`${num(plan?.covered_homes)} of ${num(plan?.footprint_homes)} owner-occupied homes in the approved ZIPs. The rest aren't dropped — broad still reaches them, just without their city's name.`}
                  />
                  <Stat
                    label="People in a city ad set"
                    value={pct(plan?.covered_population, plan?.footprint_population)}
                    hint={`${num(plan?.covered_population)} of ${num(plan?.footprint_population)} people in the approved ZIPs. The rest still see broad ads.`}
                  />
                  <Stat
                    label="Minimum daily spend"
                    value={money(totalMinimum)}
                    hint="Facebook rejects a campaign whose daily budget can't fund its ad sets — about $1.01 each. This is the floor to keep them eligible, not your budget."
                  />
                </Stack>
                {loading && <LinearProgress sx={{ mt: 2 }} />}

                {plan?.warnings?.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>Worth fixing before publishing</AlertTitle>
                    {plan.warnings.map((w) => (
                      <Typography key={w} variant="body2">
                        {w}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {campaigns.length > 0 && (
                  <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Territory</TableCell>
                          <TableCell align="right">City ad sets</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Share of the territory's owner-occupied homes that sit in a city with its own ad set.">
                              <span>Homes covered</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Share of the territory's population that sits in a city with its own ad set.">
                              <span>People covered</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">Min daily</TableCell>
                          <TableCell>Largest city</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {campaigns.map((c) => {
                          const conc = concentration(c);
                          const heavy = conc && conc.share >= CONCENTRATION_FLAG;
                          return (
                            <TableRow key={c.campaign_name} hover>
                              <TableCell>{c.territory}</TableCell>
                              <TableCell align="right">{num(c.ad_sets?.length)}</TableCell>
                              <TableCell align="right">
                                <Tooltip title={`${num(c.covered_homes)} of ${num(c.footprint_homes)} homes`}>
                                  <span>{pct(c.covered_homes, c.footprint_homes)}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={`${num(c.covered_population)} of ${num(c.footprint_population)} people`}>
                                  <span>{pct(c.covered_population, c.footprint_population)}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">{money(c.min_daily_budget_cents)}</TableCell>
                              <TableCell>
                                {conc && (
                                  <Tooltip
                                    title={
                                      heavy
                                        ? 'Most of this campaign is one city, so if that city turns, the whole campaign turns with it.'
                                        : ''
                                    }
                                  >
                                    <Chip
                                      size="small"
                                      label={`${conc.city} · ${Math.round(conc.share * 100)}%`}
                                      color={heavy ? 'warning' : 'default'}
                                      variant={heavy ? 'filled' : 'outlined'}
                                    />
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {!locked && pagePicker}

                <Stack direction="row" spacing={1} sx={{ mt: 3 }} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    disabled={locking || !adAccountId || !campaigns.length}
                    onClick={handleLock}
                  >
                    {locking ? <CircularProgress size={20} /> : `Lock ${num(plan?.units)} cities`}
                  </Button>
                  {changingPlan && (
                    <Button onClick={() => setChangingPlan(false)} disabled={locking}>
                      Cancel
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Locking only saves the cities. Nothing is created on Facebook until ads are published.
                  </Typography>
                </Stack>
              </>
            )}
          </Step>

          {/* ── Step 2 ───────────────────────────────────────────── */}
          <Step
            n={2}
            title="Creative app makes an ad for each city"
            state={!locked ? 'blocked' : lockedCities && withAds === lockedCities ? 'done' : 'waiting'}
            stateLabel={
              !locked
                ? 'Waiting on step 1'
                : `${num(withAds)} of ${num(lockedCities)} cities live${pendingAds ? ` · ${num(pendingAds)} waiting to publish` : ''}`
            }
          >
            <Typography variant="body2" color="text.secondary">
              {!locked
                ? 'Once the plan is locked, the creative app reads this client’s city list and makes one ad for each city.'
                : 'The creative app reads this client’s city list and sends one ad per city — up to 10 images and 5 headlines each, which Facebook mixes on its own. Nothing to do here; the ads it sends appear in step 3.'}
            </Typography>
            {locked && lockedCities > 0 && (
              <LinearProgress
                variant="determinate"
                value={(withAds / lockedCities) * 100}
                sx={{ mt: 2, height: 6, borderRadius: 3, maxWidth: 480 }}
              />
            )}
          </Step>

          {/* ── Step 3 ───────────────────────────────────────────── */}
          <Step
            n={3}
            title="Review and publish"
            state={clientBatches.length ? 'todo' : 'waiting'}
            stateLabel={
              clientBatches.length
                ? `${clientBatches.length} batch${clientBatches.length === 1 ? '' : 'es'} to publish`
                : 'Nothing to publish yet'
            }
          >
            {locked && pagePicker}
            {locked && (plan?.start_budgets || []).map((budgets) => (
              <StartBudgets
                key={budgets.plan_id}
                budgets={budgets}
                showVertical={(plan?.start_budgets || []).length > 1}
                onSaved={(saved) => setPlan((p) => ({
                  ...p,
                  start_budgets: (p?.start_budgets || []).map((b) => (b.plan_id === saved.plan_id ? saved : b)),
                }))}
              />
            ))}
            <Typography variant="body2" color="text.secondary" sx={{ mt: locked ? 2 : 0, mb: clientBatches.length ? 2 : 0 }}>
              Publishing creates a city&apos;s campaign and ad set the first time it
              gets an ad. Territory campaigns are created <b>paused</b>, at their
              start budget — turning a
              campaign on in Ads Manager is the one switch that takes it live. A new
              ad for a city that already has one goes into a fresh ad set, and the
              old ad set is paused. Publishing runs in the background; videos wait
              for Facebook to finish processing them first.
            </Typography>
            {withAds > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                {lockedPlans.filter((p) => p.with_ads > 0).map((p) => (
                  <Button
                    key={p.plan_id}
                    size="small"
                    variant="outlined"
                    disabled={rebuilding || publishing}
                    onClick={() => handleRebuild(p.plan_id)}
                  >
                    {rebuilding ? <CircularProgress size={16} /> : `Rebuild ${num(p.with_ads)} published ${lockedPlans.length > 1 ? `${p.vertical} ` : ''}ads`}
                  </Button>
                ))}
                <Typography variant="caption" color="text.secondary">
                  Remakes the live ads from what we stored — same copy and images — after a
                  change to how ads are built. Stages a batch; nothing changes until you publish it.
                </Typography>
              </Stack>
            )}
            {clientBatches.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing from the creative app yet.
              </Typography>
            ) : (
              <Stack spacing={2} divider={<Divider flexItem />}>
                {clientBatches.map((batch) => {
                  const summary = batchSummary(batch);
                  const isPublishing = batch.status === 'publishing';
                  const firstFailure = summary.failed.find((r) => r.error);
                  return (
                    <Box key={batch.id}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={1}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                              {num(summary.ads)} ads · batch {batch.external_id}
                            </Typography>
                            {summary.videos > 0 && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${num(summary.videos)} video${summary.videos === 1 ? '' : 's'}`}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {[...new Set((batch.rows || []).map((r) => r.raw_city).filter(Boolean))]
                              .slice(0, 6)
                              .join(', ')}
                            {(batch.rows || []).length > 6 ? '…' : ''}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            disabled={isPublishing}
                            onClick={() => handlePublish(batch, true)}
                          >
                            Dry run
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={batch.unresolved > 0 || isPublishing}
                            onClick={() => handlePublish(batch, false)}
                          >
                            {batch.status === 'failed' ? 'Retry failed' : 'Publish'}
                          </Button>
                        </Stack>
                      </Stack>
                      {isPublishing && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={summary.ads ? (summary.live / summary.ads) * 100 : 0}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {num(summary.live)} of {num(summary.ads)} live · publishing in the background
                            {summary.videos > 0 ? ' (videos wait for Facebook to process them)' : ''}
                          </Typography>
                        </Box>
                      )}
                      {batch.status === 'failed' && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {batch.notes ||
                            `${num(summary.failed.length)} of ${num(summary.ads)} ads failed to publish.`}
                          {firstFailure ? ` ${firstFailure.raw_city}: ${firstFailure.error}` : ''}
                          {' '}Retrying publishes only the ads that have not gone live.
                        </Alert>
                      )}
                      {batch.unresolved > 0 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {batch.unresolved} of these ads don&apos;t match a city in the
                          locked plan, so publishing is blocked. The creative app should
                          re-read the city list and send them again.
                        </Alert>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
            {finishedBatches.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  Finished
                </Typography>
                <Stack spacing={0.5}>
                  {finishedBatches.map((b) => (
                    <Stack key={b.id} direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        variant="outlined"
                        color={b.outcome === 'published' ? 'success' : 'default'}
                        label={b.outcome === 'published' ? `Published · ${num(b.live)} live` : 'Replaced by a newer push'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {num((b.rows || []).length)} ads · batch {b.external_id}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Step>
        </>
      )}
    </Stack>
  );
}

const STATE_CHIP = {
  done: { color: 'success', variant: 'filled' },
  todo: { color: 'primary', variant: 'filled' },
  waiting: { color: 'default', variant: 'outlined' },
  blocked: { color: 'default', variant: 'outlined' },
};

function Step({ n, title, state, stateLabel, children }) {
  const muted = state === 'blocked';
  return (
    <Card variant="outlined" sx={{ opacity: muted ? 0.7 : 1 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: 14,
              bgcolor: state === 'done' ? 'success.main' : 'action.selected',
              color: state === 'done' ? 'success.contrastText' : 'text.primary',
              flexShrink: 0,
            }}
          >
            {state === 'done' ? '✓' : n}
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          <Chip size="small" label={stateLabel} {...STATE_CHIP[state]} />
        </Stack>
        <Box sx={{ pl: { xs: 0, sm: 5.5 } }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

const START_NOTE = {
  held: {
    label: 'Bid cut in place',
    color: 'default',
    hint: 'A staff bid cut is set on this territory in the optimizer, so it opens at the minimum.',
  },
  weak: {
    label: 'Consider leaving off',
    color: 'warning',
    hint: 'Cost of marketing here runs more than twice the account’s, so it opens at the minimum.',
  },
  new: {
    label: 'Sized on homes only',
    color: 'default',
    hint: 'Not enough sales history to judge its cost of marketing yet, so only its size counts.',
  },
};

const dollars = (cents) => `$${Math.round((cents || 0) / 100).toLocaleString()}`;
const START_ROUND_CENTS = 2500;
const START_SLIDER_STEP = 5000;

// The server's split (local_start_budget.allocate), run here so the slider and
// typed budgets answer instantly. Saving returns the server's own numbers.
function splitStartBudgets(rows, potCents, pins) {
  const out = rows.map((r) => {
    const custom = pins[r.territory] != null;
    return {
      ...r,
      custom,
      start_cents: custom ? Math.max(pins[r.territory], r.floor_cents) : r.floor_cents,
    };
  });
  if (!potCents) return out;
  const byName = Object.fromEntries(out.map((r) => [r.territory, r]));
  const fixed = new Set(out.filter((r) => r.custom || r.weight == null).map((r) => r.territory));
  const weights = Object.fromEntries(
    out.filter((r) => !fixed.has(r.territory)).map((r) => [r.territory, r.weight]),
  );
  let shares = {};
  while (Object.keys(weights).length) {
    const names = Object.keys(weights);
    const room = potCents - [...fixed].reduce((sum, n) => sum + byName[n].start_cents, 0);
    const total = names.reduce((sum, n) => sum + weights[n], 0);
    shares = Object.fromEntries(
      names.map((n) => [n, total > 0 ? (room * weights[n]) / total : room / names.length]),
    );
    const short = names.filter((n) => shares[n] < byName[n].floor_cents);
    if (!short.length) break;
    short.forEach((n) => {
      fixed.add(n);
      delete weights[n];
    });
    shares = {};
  }
  Object.entries(shares).forEach(([n, share]) => {
    byName[n].start_cents = Math.max(
      Math.round(share / START_ROUND_CENTS) * START_ROUND_CENTS,
      byName[n].floor_cents,
    );
  });
  return out;
}

function savedPins(budgets) {
  return Object.fromEntries(
    (budgets.territories || []).filter((r) => r.custom).map((r) => [r.territory, r.start_cents]),
  );
}

function StartBudgets({ budgets, showVertical, onSaved }) {
  const message = useMessage();
  const recommended = budgets.recommended_pot_cents;
  const [total, setTotal] = useState(budgets.custom_total ? budgets.pot_cents : null);
  const [pins, setPins] = useState(() => savedPins(budgets));
  const [drafts, setDrafts] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // A fresh answer from the server (load or save) replaces local edits.
  useEffect(() => {
    setTotal(budgets.custom_total ? budgets.pot_cents : null);
    setPins(savedPins(budgets));
    setDrafts({});
    setDirty(false);
  }, [budgets]);

  const base = budgets.territories || [];
  const pot = total ?? recommended;
  const rows = useMemo(() => splitStartBudgets(base, pot, pins), [base, pot, pins]);
  if (!base.length) return null;

  const sum = rows.reduce((acc, r) => acc + (r.start_cents || 0), 0);
  const minTotal = base.reduce((acc, r) => acc + (r.floor_cents || 0), 0);
  const sliderMax = Math.ceil(
    Math.max((recommended || 0) * 3, minTotal * 2) / START_SLIDER_STEP,
  ) * START_SLIDER_STEP;
  const sliderValue = Math.min(Math.max(pot ?? minTotal, minTotal), sliderMax);
  const share = budgets.vertical_daily_cents ? Math.round((pot / budgets.vertical_daily_cents) * 100) : null;
  const edited = total != null || Object.keys(pins).length > 0;
  const com = (value) => (value == null ? '—' : `${(value * 100).toFixed(1)}%`);

  const commitDraft = (territory) => {
    const raw = drafts[territory];
    if (raw === undefined) return;
    setDrafts(({ [territory]: _, ...rest }) => rest);
    const value = Number(raw);
    setPins(({ [territory]: _, ...rest }) => (
      raw === '' || Number.isNaN(value) ? rest : { ...rest, [territory]: Math.round(value) * 100 }
    ));
    setDirty(true);
  };

  const unpin = (territory) => {
    setPins(({ [territory]: _, ...rest }) => rest);
    setDirty(true);
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await saveLocalStartBudgets({ plan_id: budgets.plan_id, ...payload });
      onSaved(data);
      message.success('Start budgets saved. Territories not yet published open at these numbers.');
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not save the start budgets.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Start budgets{showVertical ? ` · ${budgets.vertical}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 760 }}>
        {recommended
          ? `Recommended: ${dollars(recommended)}/day, the local ${budgets.local_pct}% of ${dollars(budgets.vertical_daily_cents)}/day in the optimizer’s latest run. `
          : 'The optimizer hasn’t run for this client yet, so there’s no recommended total — set one below. '}
        Bigger territories get more, nudged by their cost of marketing (account{' '}
        {com(budgets.account_com)}). Type a territory&apos;s budget to fix it; the rest share what&apos;s
        left. Each campaign is created at its number and the optimizer keeps it at least there for{' '}
        {budgets.hold_days} days.
      </Typography>

      <Box sx={{ px: 1, maxWidth: 640, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="caption" color="text.secondary">Local budget per day</Typography>
          <Typography variant="body2" fontWeight={700}>
            {dollars(pot ?? minTotal)}/day{share != null ? ` · ${share}% of ${budgets.vertical}` : ''}
          </Typography>
        </Stack>
        <Slider
          value={sliderValue}
          min={minTotal}
          max={sliderMax}
          step={START_SLIDER_STEP}
          marks={recommended ? [{ value: recommended, label: 'Recommended' }] : []}
          onChange={(_, v) => {
            // The recommendation rarely sits on a step; snapping to it keeps
            // "back to recommended" reachable by dragging.
            const nearRecommended = recommended && Math.abs(v - recommended) < START_SLIDER_STEP / 2;
            setTotal(nearRecommended ? null : v);
            setDirty(true);
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={dollars}
          aria-label="Local budget per day"
        />
        {recommended && pot > recommended && (
          <Typography variant="caption" color="text.secondary" display="block">
            Above the recommendation, broad gives up the difference while the {budgets.hold_days}-day
            hold lasts.
          </Typography>
        )}
      </Box>

      <TableContainer sx={{ overflowX: 'auto', mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Territory</TableCell>
              <TableCell align="right">Cities</TableCell>
              <TableCell align="right">Homes</TableCell>
              <TableCell align="right">
                <Tooltip title="Ad spend ÷ revenue on leads 45–180 days old, so sales have had time to close.">
                  <span>Cost of marketing</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Start / day</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const note = START_NOTE[r.note];
              return (
                <TableRow key={r.territory} hover>
                  <TableCell>{r.territory}</TableCell>
                  <TableCell align="right">{num(r.cities)}</TableCell>
                  <TableCell align="right">{num(r.homes)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={`${dollars(r.spend * 100)} spend · ${dollars(r.revenue * 100)} revenue · ${num(r.sales)} sales`}>
                      <span>{com(r.com)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {r.campaign_id ? (
                      dollars(r.start_cents)
                    ) : (
                      <Tooltip title={`Recommended ${dollars(r.recommended_cents)} · minimum ${dollars(r.floor_cents)}`}>
                        <TextField
                          size="small"
                          type="number"
                          value={drafts[r.territory] ?? Math.round(r.start_cents / 100)}
                          onChange={(e) => setDrafts((d) => ({ ...d, [r.territory]: e.target.value }))}
                          onBlur={() => commitDraft(r.territory)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitDraft(r.territory);
                          }}
                          inputProps={{ min: Math.round(r.floor_cents / 100), step: 25, 'aria-label': `${r.territory} start budget` }}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          sx={{ width: 130 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {r.custom && !r.campaign_id && (
                        <Tooltip title="Typed in. Remove to go back to the split.">
                          <Chip size="small" label="Custom" color="primary" variant="outlined" onDelete={() => unpin(r.territory)} />
                        </Tooltip>
                      )}
                      {note && (
                        <Tooltip title={note.hint}>
                          <Chip size="small" label={note.label} color={note.color} variant="outlined" />
                        </Tooltip>
                      )}
                      {r.campaign_id && (
                        <Tooltip title="Already created on Facebook. Its budget was set when it was created; change it in Budget Optimizer settings.">
                          <Chip size="small" label="Created" variant="outlined" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell colSpan={3} />
              <TableCell align="right" sx={{ fontWeight: 700 }}>{dollars(sum)}</TableCell>
              <TableCell>
                {pot && sum > pot + START_ROUND_CENTS * rows.length && (
                  <Typography variant="caption" color="warning.main">
                    Typed and minimum budgets add up to more than the {dollars(pot)} total.
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <Button
          variant="contained"
          size="small"
          disabled={!dirty || saving}
          onClick={() => save({ total_cents: total, overrides: pins })}
        >
          {saving ? <CircularProgress size={18} /> : 'Save start budgets'}
        </Button>
        <Button
          size="small"
          disabled={saving || (!edited && !budgets.custom_total && !Object.keys(savedPins(budgets)).length)}
          onClick={() => save({ total_cents: null, overrides: {} })}
        >
          Reset to recommended
        </Button>
        {dirty && (
          <Typography variant="caption" color="text.secondary">
            Unsaved — publishing uses the last saved numbers.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function Stat({ label, value, hint }) {
  const body = (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
  return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
}

function PagePicker({
  clientId, pages, verticals, split, candidates, locked, onToggle, onRename, onRefresh, refreshing,
}) {
  const readAt = pages.map((p) => p.volume_synced_at).filter(Boolean).sort().pop();
  const single = verticals.length === 1;
  const neededFor = (v) => {
    if (locked && split?.pages_needed_by_vertical) {
      return split.pages_needed_by_vertical.find((n) => n.vertical === v.vertical)?.more_pages || 0;
    }
    return v.pages_needed || 0;
  };
  const territoriesFor = (v) =>
    locked && split?.territories?.length
      ? split.territories
        .filter((t) => t.vertical === v.vertical)
        .map((t) => ({ territory: t.territory, cities: t.cities, page_name: t.page_name, fixed: t.fixed }))
      : (v.campaigns || []).map((c) => ({
        territory: c.territory, cities: (c.ad_sets || []).length, page_name: c.page_name, fixed: false,
      }));
  const anyShort = verticals.some((v) => neededFor(v) > 0);

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={600}>
          Which Facebook Pages should local ads use?
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? 'Reading Facebook…' : 'Refresh from Facebook'}
          </Button>
          <Button size="small" component={RouterLink} to={`${paths.facebook.pages}?client=${clientId}`}>
            Add or manage Pages
          </Button>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Local ads go on their own Pages — as many as the plan needs (a new Page holds 250 ads). Keep
        verticals on separate Pages where you can. Each territory stays whole on one Page.{' '}
        {readAt
          ? `Ad counts read from Facebook ${new Date(readAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}; Publish checks again.`
          : 'Ad counts are our own estimate until Facebook is read.'}
      </Typography>

      <Stack spacing={3}>
        {verticals.map((v) => {
          const needed = neededFor(v);
          const usable = pages;
          const territories = territoriesFor(v);
          return (
            <Box key={v.vertical}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>{v.vertical}</Typography>
                <Chip
                  size="small"
                  color={needed ? 'warning' : 'success'}
                  label={needed ? `Needs ${needed} more local Page${needed === 1 ? '' : 's'}` : 'Enough local Pages'}
                />
              </Stack>
              <Stack spacing={1.5} sx={{ maxWidth: 680 }}>
                {usable.map((page) => (
                  <PageRow
                    key={page.page_id}
                    page={page}
                    checked={page.reserved_for_local && (
                      (page.local_vertical_ids || []).includes(v.vertical_id)
                      || (!(page.local_vertical_ids || []).length && single)
                    )}
                    otherVerticals={(page.local_vertical_names || []).filter((name) => name !== v.vertical)}
                    verticalName={v.vertical}
                    onToggle={(use) => onToggle(page.page_id, v.vertical_id, use)}
                    onRename={onRename}
                  />
                ))}
              </Stack>
              {territories.length > 0 && (
                <TableContainer sx={{ mt: 1.5, maxWidth: 680, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Territory</TableCell>
                        <TableCell align="right">Cities</TableCell>
                        <TableCell>Local Page</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {territories.map((t) => (
                        <TableRow key={t.territory}>
                          <TableCell>{t.territory}</TableCell>
                          <TableCell align="right">{num(t.cities)}</TableCell>
                          <TableCell>
                            {t.page_name ? (
                              <>
                                {t.page_name}
                                {t.fixed && (
                                  <Tooltip title="This territory has published on this Page, so it stays here.">
                                    <Chip size="small" variant="outlined" label="fixed" sx={{ ml: 1 }} />
                                  </Tooltip>
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="warning.main">
                                Doesn&apos;t fit on the chosen Pages — add one
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
        })}
      </Stack>

      {anyShort && candidates.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Pages you could set up for local
          </Typography>
          <Stack spacing={0.5}>
            {candidates.map((c) => (
              <Stack key={c.page_id} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{c.page_name}</Typography>
                <Chip size="small" variant="outlined" label={c.kind} />
                <Typography variant="caption" color="text.secondary">holds {num(c.ad_limit)} ads</Typography>
                <Button
                  size="small"
                  component={RouterLink}
                  to={`${paths.facebook.pages}?setup=${c.id}&setup_client=${clientId}`}
                >
                  Set up
                </Button>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function PageRow({ page, checked, verticalName, otherVerticals = [], onToggle, onRename }) {
  const [nickname, setNickname] = useState(page.nickname || '');

  useEffect(() => {
    setNickname(page.nickname || '');
  }, [page.nickname]);

  const save = () => {
    if ((page.nickname || '') !== nickname.trim()) onRename(page.page_id, nickname.trim());
  };

  return (
    <Box sx={{ border: 1, borderColor: checked ? 'primary.main' : 'divider', borderRadius: 1.5, p: 1.5 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Tooltip title={checked ? `Stop using for ${verticalName} local ads` : `Use for ${verticalName} local ads`}>
          <Checkbox size="small" checked={checked} onChange={(e) => onToggle(e.target.checked)} sx={{ mt: -0.5 }} />
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={600}>
              {page.display_name}
            </Typography>
            {checked && <Chip size="small" color="primary" label={`${verticalName} local`} />}
            {otherVerticals.map((name) => (
              <Chip key={name} size="small" variant="outlined" label={`${name} local`} />
            ))}
            {page.status === 'full' && <Chip size="small" color="error" label="Full" />}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
            Page ID {page.page_id}
            {page.nickname ? ` · Facebook name: ${page.page_name}` : ''}
            {' · '}
            <a href={page.facebook_url} target="_blank" rel="noreferrer">Open on Facebook</a>
            {page.ads_library_url && (
              <>
                {' · '}
                <a href={page.ads_library_url} target="_blank" rel="noreferrer">
                  Ads Library
                </a>
              </>
            )}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Nickname"
              placeholder="e.g. Local — Texas"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              inputProps={{ maxLength: 100 }}
              sx={{ minWidth: 220 }}
            />
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Ads on this Page
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {num(page.ads_used)} / {num(page.ad_tier)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(page.usage_pct, 100)}
                color={page.status === 'full' ? 'error' : page.status === 'warning' ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
              />
            </Box>
          </Stack>
          {checked && otherVerticals.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This Page also carries {otherVerticals.join(', ')} local ads, so leads will see {verticalName} and{' '}
              {otherVerticals.join(', ')} ads on the same Page.
            </Alert>
          )}
          {page.client_mismatch && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Facebook says none of the {num(page.ads_used)} ads on this Page run from this
              client&apos;s ad accounts
              {page.ads_mostly_from ? ` — most run from ${page.ads_mostly_from}'s` : ''}. It may
              be another client&apos;s Page; check before publishing under it.
            </Alert>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
